import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Car, CheckCircle, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import PriceEstimateNote from "@/components/PriceEstimateNote";
import { suggestVehicles } from "@/lib/vehicle";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import LegalAcceptCheckbox from "@/components/LegalAcceptCheckbox";
import { useI18n } from "@/lib/i18n";

const EMPTY = {
  client_name: "", client_phone: "", client_email: "",
  pickup_date: "", pickup_time: "",
  pickup_location: "", dropoff_location: "",
  passenger_count: 1, luggage_count: 0,
  vehicle_type: "eco", service_type: "transfert",
  notes: "",
  is_round_trip: false, return_date: "", return_time: "",
  accepted_terms: false,
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function PublicRequestPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  // Phase 28 — tenant attribution. When the user lands here from a branded
  // `/{slug}/booking` page, `CompanyRedirect` forwards the slug as
  // `?company=<slug>`. We forward it to the backend so the resulting
  // ride_request is stored under the correct `company_id` and NEVER
  // silently falls back to the default tenant.
  const companySlug = (searchParams.get("company") || "").trim().toLowerCase();
  // Phase 28.1 — branded header. When a `?company=<slug>` is present we
  // load the tenant's public company doc and pass it to PublicHeader so
  // the page reflects the actual operator (logo + name + accent colour)
  // instead of the S.DRIVE default. Unknown/suspended slug → we silently
  // fall back to no branding override (server-side POST still rejects
  // with 404, so no data can be mis-attributed).
  const [company, setCompany] = useState(null);
  useEffect(() => {
    if (!companySlug) { setCompany(null); return; }
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/companies/${encodeURIComponent(companySlug)}`);
        if (!cancel) setCompany({ ...data, slug: companySlug });
      } catch {
        if (!cancel) setCompany(null);
      }
    })();
    return () => { cancel = true; };
  }, [companySlug]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(null);
  const [estimate, setEstimate] = useState(null);   // {price, source}
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  // Live auto-estimate as soon as pickup + dropoff + vehicle are filled.
  useEffect(() => {
    const p = (form.pickup_location || "").trim();
    const d = (form.dropoff_location || "").trim();
    if (!p || !d || form.vehicle_type === "moto") { setEstimate(null); return; }
    const ctrl = new AbortController();
    const id = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/public/pricing-match`, {
          params: {
            from_location: p,
            to_location: d,
            vehicle_type: form.vehicle_type,
            ...(companySlug ? { company_slug: companySlug } : {}),
          },
          signal: ctrl.signal,
        });
        if (data?.match?.default_price != null) {
          setEstimate({ price: data.match.default_price, source: data.source });
        } else {
          setEstimate(null);
        }
      } catch { /* silent */ }
    }, 450);
    return () => { clearTimeout(id); ctrl.abort(); };
  }, [form.pickup_location, form.dropoff_location, form.vehicle_type, companySlug]);

  const setF = (k) => (e) => setForm((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    // Name + phone + email are now all required for reliable follow-up.
    const required = ["client_name", "client_phone", "client_email", "pickup_date", "pickup_time", "pickup_location", "dropoff_location"];
    for (const k of required) {
      if (!(form[k] || "").toString().trim()) { toast.error(t("pub.err_required")); return; }
    }
    if (!EMAIL_RE.test(form.client_email.trim())) { toast.error(t("pub.err_email")); return; }
    if (!form.accepted_terms) {
      toast.error("Vous devez accepter les conditions générales");
      return;
    }
    // Moto rules
    const body = { ...form, passenger_count: Number(form.passenger_count || 1), luggage_count: Number(form.luggage_count || 0) };
    if (body.vehicle_type === "moto") { body.passenger_count = 1; body.luggage_count = 0; }
    // Phase 28 — attach the branded tenant if this form was opened through
    // a `/{slug}/booking` path. Absence of `company` = S.DRIVE default
    // (handled server-side).
    if (companySlug) body.company_slug = companySlug;

    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/public/ride-requests`, body);
      setSent(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("pub.err_send"));
    } finally { setSaving(false); }
  };

  if (sent) {
    const ref = sent.reference || "";
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col">
        <PublicHeader langTestId="public-sent-lang" company={company} />
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div data-testid="public-request-sent" className="max-w-md w-full bg-[#121212] border border-zinc-800 rounded-xl p-6 sm:p-8 text-center">            <div className="flex justify-center mb-4">
              <CheckCircle size={48} weight="fill" className="text-emerald-500" />
            </div>
            <h2 className="text-xl font-medium text-white mb-2">Votre demande a bien été envoyée.</h2>
            <p className="text-sm text-zinc-400 mb-5">
              Créez votre espace client pour suivre votre demande et consulter votre devis.
            </p>

            {/* Quote reference — bold, copy-friendly */}
            {ref && (
              <div data-testid="public-sent-reference-block" className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-4 mb-5">
                <p className="text-[11px] uppercase tracking-wider text-amber-400 font-semibold mb-1">Votre numéro de devis</p>
                <p data-testid="public-sent-reference" className="text-2xl font-bold text-white tracking-wide select-all">{ref}</p>
                <p className="text-[11px] text-zinc-500 mt-2">Conservez ce numéro, il vous permettra de retrouver votre demande.</p>
              </div>
            )}

            {/* Primary action: create the client account with prefilled data */}
            <Button
              data-testid="public-sent-create-account"
              onClick={() => navigate("/register-client", { state: { prefill: {
                client_name: form.client_name,
                client_email: form.client_email,
                client_phone: form.client_phone,
                reference: ref,
              } } })}
              className="w-full bg-amber-500 text-black font-semibold hover:bg-amber-600 h-11"
            >
              Créer mon espace client
            </Button>

            {/* Secondary action — only shown for users who already have one */}
            <Button
              data-testid="public-sent-have-account"
              variant="ghost"
              onClick={() => navigate("/login", { state: { prefill_email: form.client_email } })}
              className="w-full mt-2 text-zinc-400 hover:text-white"
            >
              J'ai déjà un compte
            </Button>

            {/* Tertiary: send another (kept for power users) */}
            <button
              data-testid="public-send-again"
              onClick={() => { setSent(null); setForm(EMPTY); }}
              className="text-[11px] text-zinc-500 hover:text-zinc-300 mt-4 block mx-auto"
            >
              Envoyer une autre demande
            </button>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div data-testid="public-request-page" className="min-h-screen bg-[#050505]">
      <PublicHeader langTestId="public-request-lang" company={company} />

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-white flex items-center gap-2"><Car size={26} className="text-amber-500" /> {t("pub.title")}</h2>
          <p className="text-sm text-zinc-400 mt-1">{t("pub.intro_1")} <span className="text-amber-400">{t("pub.intro_accent")}</span> {t("pub.intro_2")}</p>
        </div>

        <form onSubmit={submit} data-testid="public-request-form" className="space-y-4 bg-[#121212] border border-zinc-800 rounded-lg p-5">
          {/* Optional AI quick-parse — paste a natural-language sentence and let the system prefill most fields */}
          <div className="space-y-1.5 pb-3 border-b border-zinc-800/80">
            <Label className="text-xs text-zinc-400 uppercase tracking-wider flex items-center gap-1.5">
              <Sparkle size={12} weight="fill" className="text-amber-400" /> {t("pub.ai_label")}
            </Label>
            <Textarea
              data-testid="pub-ai-text"
              rows={2}
              value={aiText}
              onChange={(e) => setAiText(e.target.value)}
              placeholder={t("pub.ai_placeholder")}
              className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600"
            />
            <div className="flex items-center gap-2">
              <Button
                type="button"
                data-testid="pub-ai-apply"
                disabled={aiBusy || !aiText.trim()}
                onClick={async () => {
                  setAiBusy(true);
                  try {
                    const { data } = await axios.post(`${API}/public/ai-parse`, { text: aiText.trim() });
                    const patch = {};
                    if (data?.pickup_date) patch.pickup_date = data.pickup_date;
                    if (data?.pickup_time) patch.pickup_time = data.pickup_time;
                    if (data?.pickup_location) patch.pickup_location = data.pickup_location;
                    if (data?.dropoff_location) patch.dropoff_location = data.dropoff_location;
                    // Fallback: split "A → B" from route_label when pickup/dropoff are not separately provided
                    if ((!patch.pickup_location || !patch.dropoff_location) && data?.route_label && data.route_label.includes("→")) {
                      const [a, b] = data.route_label.split("→").map((s) => s.trim());
                      if (!patch.pickup_location && a) patch.pickup_location = a;
                      if (!patch.dropoff_location && b) patch.dropoff_location = b;
                    }
                    if (data?.passenger_count) patch.passenger_count = data.passenger_count;
                    if (data?.luggage_count != null) patch.luggage_count = data.luggage_count;
                    const vt = data?.vehicle_type || data?.ride_type;
                    if (vt && ["eco", "van", "moto"].includes(vt)) patch.vehicle_type = vt;
                    if (data?.client_name && !form.client_name) patch.client_name = data.client_name;
                    if (data?.client_phone && !form.client_phone) patch.client_phone = data.client_phone;
                    if (Object.keys(patch).length === 0) { toast.info(t("pub.ai_none")); }
                    else { setForm((prev) => ({ ...prev, ...patch })); toast.success(t("pub.ai_ok")); }
                  } catch {
                    toast.error(t("pub.ai_fail"));
                  } finally { setAiBusy(false); }
                }}
                className="h-9 bg-amber-500 text-black font-semibold hover:bg-amber-600 text-xs"
              >
                <Sparkle size={13} weight="fill" className="mr-1" /> {aiBusy ? t("pub.ai_applying") : t("pub.ai_apply")}
              </Button>
              <p className="text-[10px] text-zinc-500">{t("pub.ai_hint")}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={t("pub.name")}><Input data-testid="pub-name" required value={form.client_name} onChange={setF("client_name")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></Field>
            <Field label={t("pub.phone")}><Input data-testid="pub-phone" required type="tel" placeholder="+33..." value={form.client_phone} onChange={setF("client_phone")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></Field>
          </div>
          <Field label={t("pub.email")}><Input data-testid="pub-email" required type="email" value={form.client_email} onChange={setF("client_email")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t("pub.date")}><Input data-testid="pub-date" required type="date" value={form.pickup_date} onChange={setF("pickup_date")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></Field>
            <Field label={t("pub.time")}><Input data-testid="pub-time" required type="time" value={form.pickup_time} onChange={setF("pickup_time")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></Field>
          </div>

          <Field label={t("pub.pickup")}><Input data-testid="pub-pickup" required value={form.pickup_location} onChange={setF("pickup_location")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></Field>
          <Field label={t("pub.dropoff")}><Input data-testid="pub-dropoff" required value={form.dropoff_location} onChange={setF("dropoff_location")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></Field>

          {estimate && (
            <PriceEstimateNote
              price={estimate.price}
              source={estimate.source}
              data-testid="public-price-estimate"
            />
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label={t("pub.vehicle")}>
              <Select value={form.vehicle_type} onValueChange={(v) => setForm(p => ({ ...p, vehicle_type: v, ...(v === "moto" ? { passenger_count: 1, luggage_count: 0 } : {}) }))}>
                <SelectTrigger data-testid="pub-vehicle" className="bg-[#1A1A1A] border-zinc-700 text-white h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-zinc-700 text-white">
                  <SelectItem value="eco">Eco</SelectItem>
                  <SelectItem value="van">Van</SelectItem>
                  <SelectItem value="moto">Moto</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={t("pub.service")}>
              <Select value={form.service_type} onValueChange={(v) => setForm(p => ({ ...p, service_type: v }))}>
                <SelectTrigger data-testid="pub-service" className="bg-[#1A1A1A] border-zinc-700 text-white h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-zinc-700 text-white">
                  <SelectItem value="transfert">{t("pub.service.transfert")}</SelectItem>
                  <SelectItem value="mise_a_disposition">{t("pub.service.mad")}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t("pub.pax")}>
              <Input data-testid="pub-pax" type="number" min="1"
                     max={form.vehicle_type === "moto" ? 1 : undefined}
                     value={form.passenger_count} onChange={setF("passenger_count")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" disabled={form.vehicle_type === "moto"} />
            </Field>
            <Field label={t("pub.lug")}>
              <Input data-testid="pub-lug" type="number" min="0"
                     max={form.vehicle_type === "moto" ? 0 : undefined}
                     value={form.luggage_count} onChange={setF("luggage_count")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" disabled={form.vehicle_type === "moto"} />
            </Field>
          </div>
          {form.vehicle_type !== "moto" && parseInt(form.passenger_count || "0") >= 5 && (() => {
            const s = suggestVehicles(form.passenger_count, form.luggage_count);
            return (
              <p data-testid="pub-vehicle-suggestion" className="text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded p-2">
                {t("pub.suggestion")} {s.label}
                {s.heavy ? ` — ${t("pub.heavy_hint")}` : ""} {t("pub.confirm_team")}
              </p>
            );
          })()}
          {form.vehicle_type === "moto" && (
            <p className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded p-2" data-testid="moto-notice">
              {t("pub.moto_notice")}
            </p>
          )}

          <Field label={t("pub.notes")}>
            <Textarea data-testid="pub-notes" rows={3} value={form.notes} onChange={setF("notes")} className="bg-[#1A1A1A] border-zinc-700 text-white" />
          </Field>

          <LegalAcceptCheckbox
            checked={form.accepted_terms}
            onChange={(v) => setForm((p) => ({ ...p, accepted_terms: v }))}
            testid="pub-legal"
          />

          <Button data-testid="pub-submit" type="submit" disabled={saving} className="w-full bg-amber-500 text-black font-semibold hover:bg-amber-600 h-11">
            {saving ? t("pub.submitting") : t("pub.submit")}
          </Button>
          <p className="text-[11px] text-zinc-500 text-center">{t("pub.signup_hint")}</p>
        </form>
      </main>
      <PublicFooter />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-zinc-400 uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}
